import { useState } from 'react';
import { INDUSTRIES } from '../lib/industries.js';
import { SITE_TYPES, SITE_STATUSES } from '../lib/siteTypes.js';

const blank = {
  name: '',
  lat: '',
  lon: '',
  address: '',
  industry: 'construction',
  alertBufferM: 1000,
  elevationM: '',
  notes: '',
  type: 'jobsite',
  status: 'active',
  organization: '',
  contact: {},
  ohsJurisdiction: 'auto',
  workIntensity: '',
  airVelocity: '',
  activities: [],
};

const ACTIVITIES = [
  { key: 'tower_crane', label: 'Tower crane operation' },
  { key: 'excavation', label: 'Excavation' },
  { key: 'mine', label: 'Mine work' },
];

/**
 * Create/edit form for a saved site (every map pin is a site). Coordinates
 * are exact: entered, from the device, or from a pin dropped on the map
 * (`compact` hides the coordinate inputs' helper row). Pass `teams` to let a
 * new site choose its team.
 */
export default function SiteForm({ initial, submitLabel = 'Save site', onSubmit, onCancel, teams, compact = false }) {
  const [v, setV] = useState(() => ({
    ...blank,
    ...(initial ?? {}),
    elevationM: initial?.elevationM ?? '',
    workIntensity: initial?.workIntensity ?? '',
    airVelocity: initial?.airVelocity ?? '',
    activities: initial?.activities ?? [],
    organization: initial?.organization ?? '',
    contact: { ...(initial?.contact ?? {}) },
    teamId: initial?.teamId ?? teams?.[0]?.id,
  }));
  const setContact = (k) => (e) => setV((s) => ({ ...s, contact: { ...s.contact, [k]: e.target.value } }));
  const toggleActivity = (key) =>
    setV((s) => ({
      ...s,
      activities: s.activities.includes(key) ? s.activities.filter((a) => a !== key) : [...s.activities, key],
    }));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));

  function useMyLocation() {
    if (!navigator.geolocation) {
      setError('This browser can’t share its location — enter coordinates instead.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        setV((s) => ({ ...s, lat: pos.coords.latitude.toFixed(5), lon: pos.coords.longitude.toFixed(5) }));
      },
      (err) => {
        setLocating(false);
        setError(`Couldn’t get your location: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        name: v.name,
        lat: Number(v.lat),
        lon: Number(v.lon),
        address: v.address,
        industry: v.industry,
        alertBufferM: Number(v.alertBufferM),
        elevationM: v.elevationM === '' ? null : Number(v.elevationM),
        notes: v.notes,
        type: v.type,
        status: v.status,
        organization: v.organization,
        contact: v.contact,
        // Team is chosen on create only; moving a site between teams is a separate action.
        teamId: initial?.id ? undefined : v.teamId,
        ohsJurisdiction: v.ohsJurisdiction,
        workIntensity: v.workIntensity || null,
        airVelocity: v.airVelocity || null,
        activities: v.activities,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <label>
        Site name
        <input required maxLength={120} value={v.name} onChange={set('name')} />
      </label>
      <div className="form-row">
        <label>
          Latitude
          <input required inputMode="decimal" value={v.lat} onChange={set('lat')} placeholder="32.7157" />
        </label>
        <label>
          Longitude
          <input required inputMode="decimal" value={v.lon} onChange={set('lon')} placeholder="-117.1611" />
        </label>
      </div>
      {teams?.length > 1 && !initial?.id && (
        <label>
          Team
          <select value={v.teamId} onChange={set('teamId')}>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="form-row">
        <label>
          Type
          <select value={v.type} onChange={set('type')}>
            {Object.entries(SITE_TYPES).map(([k, t]) => (
              <option key={k} value={k}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={v.status} onChange={set('status')}>
            {SITE_STATUSES.map((k) => (
              <option key={k} value={k}>
                {k[0].toUpperCase() + k.slice(1)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!compact && <div>
        <button type="button" className="btn" onClick={useMyLocation} disabled={locating}>
          {locating ? 'Locating…' : 'Use my current location'}
        </button>
        <span className="muted form-hint">
          {' '}
          Or copy coordinates from a map app (right-click a point in most map apps).
        </span>
      </div>}
      {compact && <p className="muted form-hint">Drag the white pin on the map to adjust the position.</p>}
      <label>
        Address <span className="muted">(optional, for your reference)</span>
        <input maxLength={300} value={v.address ?? ''} onChange={set('address')} />
      </label>
      <label>
        Organization <span className="muted">(optional)</span>
        <input maxLength={200} value={v.organization ?? ''} onChange={set('organization')} />
      </label>
      <div className="form-row">
        <label>
          Contact name
          <input maxLength={200} value={v.contact.name ?? ''} onChange={setContact('name')} />
        </label>
        <label>
          Phone
          <input maxLength={200} value={v.contact.phone ?? ''} onChange={setContact('phone')} />
        </label>
        <label>
          Email
          <input type="email" maxLength={200} value={v.contact.email ?? ''} onChange={setContact('email')} />
        </label>
      </div>
      <div className="form-row">
        <label>
          Industry
          <select value={v.industry} onChange={set('industry')}>
            {INDUSTRIES.map((i) => (
              <option key={i.key} value={i.key}>
                {i.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Alert buffer (metres)
          <input type="number" min={0} max={50000} step={100} value={v.alertBufferM} onChange={set('alertBufferM')} />
        </label>
        <label>
          Elevation (m) <span className="muted">(optional)</span>
          <input type="number" min={-500} max={9000} value={v.elevationM} onChange={set('elevationM')} />
        </label>
      </div>
      <p className="muted form-hint">
        The buffer also flags official warning polygons that pass within this distance of the site, not
        only ones that contain it.
      </p>
      <fieldset className="form-fieldset">
        <legend>Work context</legend>
        <p className="muted form-hint">
          Some regulations depend on the work being done (Québec and Yukon WBGT limits by workload, BC tower-crane
          wind and temperature limits, NWT mine shelter). Leave blank if unknown: those rules then show as “needs
          site input” instead of being guessed.
        </p>
        <div className="form-row">
          <label>
            Work intensity
            <select value={v.workIntensity} onChange={set('workIntensity')}>
              <option value="">Not set</option>
              <option value="light">Light</option>
              <option value="moderate">Moderate</option>
              <option value="heavy">Heavy</option>
            </select>
          </label>
          <label>
            Air velocity at the work
            <select value={v.airVelocity} onChange={set('airVelocity')}>
              <option value="">Not set</option>
              <option value="low">Low</option>
              <option value="high">High</option>
            </select>
          </label>
        </div>
        <div className="form-checks">
          {ACTIVITIES.map((a) => (
            <label key={a.key} className="check">
              <input type="checkbox" checked={v.activities.includes(a.key)} onChange={() => toggleActivity(a.key)} />
              {a.label}
            </label>
          ))}
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={v.ohsJurisdiction === 'federal'}
            onChange={(e) => setV((s) => ({ ...s, ohsJurisdiction: e.target.checked ? 'federal' : 'auto' }))}
          />
          Federally regulated undertaking (Canada) — Canada Labour Code Part II applies instead of provincial law
        </label>
      </fieldset>
      <label>
        Description / notes <span className="muted">(optional)</span>
        <textarea rows={3} maxLength={2000} value={v.notes ?? ''} onChange={set('notes')} />
      </label>
      {error && <div className="banner err">{error}</div>}
      <div className="form-actions">
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button className="btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
